/* ==========================================================================
   Amada · 氛围轮播（sections/amada-carousel.liquid）
   --------------------------------------------------------------------------
   职责：
     1. 大图自动轮播（淡入淡出），底部选中胶囊**同步**滑到对应按钮
     2. 点按钮手动切图；切完自动播放重新计时（不是停掉）
     3. 暂停条件：hover / 键盘聚焦在组件内 / 标签页切后台 /
        用户系统开了「减少动态效果」
   --------------------------------------------------------------------------
   为什么滑块位置由 JS 算而不是 CSS translateX(index * 100%)：
     运营可以在编辑器里任意增删按钮、文案长短不一，窄屏还会折行/横向滚动。
     纯 CSS 等分只有「4 个等宽、永不折行」时才成立。这里直接读真实按钮的
     offsetLeft/offsetTop/offsetWidth/offsetHeight 写进滑块，任何布局都对。

   ⚠️ 本文件由 section 自己 <script src> 挂载，同一页面放两个本 section 时
      脚本会被执行两次 —— 所有初始化都必须幂等（见 window.__amadaCarouselBooted
      与 root.dataset.acsInit）。
   ========================================================================== */
(function () {
  'use strict';

  if (window.__amadaCarouselBooted) return;
  window.__amadaCarouselBooted = true;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReduced() {
    return !!(REDUCED && REDUCED.matches);
  }

  function AmadaCarousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-acs-track]');
    this.tabsBox = root.querySelector('[data-acs-tabs]');
    this.thumb = root.querySelector('[data-acs-thumb]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-acs-slide]'));
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-acs-tab]'));

    this.count = Math.min(this.slides.length, this.tabs.length);
    if (this.count === 0) return;

    this.index = 0;
    for (var i = 0; i < this.count; i++) {
      if (this.slides[i].classList.contains('is-active')) {
        this.index = i;
        break;
      }
    }

    this.autoplay = root.getAttribute('data-autoplay') === 'true' && this.count > 1;
    var sec = parseFloat(root.getAttribute('data-interval'));
    if (!isFinite(sec) || sec < 1) sec = 5;
    this.interval = sec * 1000;

    this.timer = null;
    this.hovering = false;
    this.focusing = false;

    this.bind();
    this.sync(this.index, true);
    this.start();
    this.watchPreload();
  }

  /**
   * 🚨 2026-08-27 运营验收：「轮播不动」的真因不是切换逻辑，是**后面几张图根本没加载**。
   *
   * 幻灯片默认 `visibility:hidden`（见 amada-carousel.css .amada-carousel__slide），
   * 而 Liquid 里非首张写的是 `loading="lazy"` —— 浏览器不会给隐藏元素里的 lazy 图
   * 发请求。实测草稿：slide0 naturalWidth=2160，slide1/2/3 全是 **0**。
   * 于是 is-active 明明在正常轮转（按钮高亮也在变），切过去却是一张空图，
   * 画面纹丝不动，看起来就像「自动轮播和点击都失效」。
   *
   * 判据留给下次：怀疑「图片没渲染」时先采 naturalWidth，别只看 src 和 class（铁律 33 同源）。
   *
   * 修法：轮播**滚入视口后**再把 lazy 改成 eager 并预热，不在 Liquid 里全写死 eager ——
   * 运营可能加十几张幻灯片，一进页面并发下十几张 2160 宽大图会拖垮首屏。
   */
  AmadaCarousel.prototype.preloadSlides = function () {
    var imgs = this.root.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var im = imgs[i];
      if (im.getAttribute('loading') === 'lazy') im.setAttribute('loading', 'eager');
      if (!im.complete || !im.naturalWidth) {
        /* 改 loading 属性在部分浏览器上不会立刻重新发请求，
           再用一个游离 Image 预热缓存做双保险（同 URL 会命中同一份缓存）。 */
        var pre = new Image();
        if (im.getAttribute('sizes')) pre.sizes = im.getAttribute('sizes');
        if (im.getAttribute('srcset')) pre.srcset = im.getAttribute('srcset');
        pre.src = im.currentSrc || im.src;
      }
    }
  };

  AmadaCarousel.prototype.watchPreload = function () {
    var self = this;
    if (typeof IntersectionObserver === 'undefined') {
      self.preloadSlides();
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          self.preloadSlides();
          io.disconnect();
          return;
        }
      },
      /* 提前一屏开始预载，滚到轮播时图已经就位，不会出现「切了但还是空」的中间态 */
      { rootMargin: '100% 0px' }
    );
    io.observe(this.root);
  };

  AmadaCarousel.prototype.bind = function () {
    var self = this;

    // ── 点按钮切图（捕获阶段注册：theme.js 的 page transition 用 body 上的
    //    事件代理接管 a[href]，200ms 后强制 location.href，无视 preventDefault。
    //    这里的 tab 是 <button> 不会被它接管，但统一用捕获阶段更保险。） ──
    this.track.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest ? e.target.closest('[data-acs-tab]') : null;
        if (!btn || !self.track.contains(btn)) return;
        e.preventDefault();
        var i = parseInt(btn.getAttribute('data-acs-tab'), 10);
        if (isNaN(i)) return;
        self.sync(i);
        self.restart(); // 手动切换后重新计时，而不是停掉自动播放
      },
      true
    );

    // ── 键盘：左右方向键在 tablist 内切换 ──
    this.track.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var dir = e.key === 'ArrowRight' ? 1 : -1;
      var next = (self.index + dir + self.count) % self.count;
      e.preventDefault();
      self.sync(next);
      self.restart();
      if (self.tabs[next]) self.tabs[next].focus();
    });

    // ── 暂停：hover / 组件内聚焦 ──
    this.root.addEventListener('mouseenter', function () {
      self.hovering = true;
      self.stop();
    });
    this.root.addEventListener('mouseleave', function () {
      self.hovering = false;
      self.start();
    });
    // 只在**键盘**聚焦时暂停：鼠标点按钮同样会 focus，
    // 若一并暂停，用户点完一次 tab 后自动播放就再也不恢复了（实测踩过）。
    this.root.addEventListener('focusin', function (e) {
      var t = e.target;
      try {
        if (t && t.matches && !t.matches(':focus-visible')) return;
      } catch (err) {
        /* 老浏览器不支持 :focus-visible，退回「聚焦即暂停」 */
      }
      self.focusing = true;
      self.stop();
    });
    this.root.addEventListener('focusout', function () {
      self.focusing = false;
      self.start();
    });

    // ── 暂停：标签页切后台（否则回来会「连跳几张」） ──
    this._onVis = function () {
      if (document.hidden) self.stop();
      else self.start();
    };
    document.addEventListener('visibilitychange', this._onVis);

    // ── 触摸滑动切图（移动端） ──
    var stage = this.root.querySelector('[data-acs-stage]');
    if (stage) {
      var sx = 0;
      var sy = 0;
      var tracking = false;
      stage.addEventListener(
        'touchstart',
        function (e) {
          if (!e.touches || e.touches.length !== 1) return;
          sx = e.touches[0].clientX;
          sy = e.touches[0].clientY;
          tracking = true;
          self.stop();
        },
        { passive: true }
      );
      stage.addEventListener(
        'touchend',
        function (e) {
          if (!tracking) return;
          tracking = false;
          var t = e.changedTouches && e.changedTouches[0];
          if (t) {
            var dx = t.clientX - sx;
            var dy = t.clientY - sy;
            // 横向位移够大且明显偏横向，才算滑动（否则是竖向滚页面）
            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
              var step = dx < 0 ? 1 : -1;
              self.sync((self.index + step + self.count) % self.count);
            }
          }
          self.start();
        },
        { passive: true }
      );
    }

    // ── 布局变化（换行、字体 swap、窗口缩放）后滑块要重新贴合 ──
    if (window.ResizeObserver) {
      this._ro = new ResizeObserver(function () {
        self.placeThumb(true);
      });
      this._ro.observe(this.track);
    } else {
      this._onResize = function () {
        self.placeThumb(true);
      };
      window.addEventListener('resize', this._onResize);
    }

    // Amiri 是 font_face 异步加载，字体到位后按钮宽度会变 → 再贴一次
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(function () {
        self.placeThumb(true);
      });
    }

    if (REDUCED && REDUCED.addEventListener) {
      REDUCED.addEventListener('change', function () {
        if (prefersReduced()) self.stop();
        else self.start();
      });
    }
  };

  /* 把滑块贴到当前选中按钮上。instant=true 时跳过过渡（首帧 / resize） */
  AmadaCarousel.prototype.placeThumb = function (instant) {
    if (!this.thumb) return;
    var tab = this.tabs[this.index];
    if (!tab) return;

    var w = tab.offsetWidth;
    var h = tab.offsetHeight;
    if (!w || !h) return; // section 被隐藏时别写 0，等下次

    var apply = function (el) {
      el.style.width = w + 'px';
      el.style.height = h + 'px';
      el.style.transform = 'translate(' + tab.offsetLeft + 'px,' + tab.offsetTop + 'px)';
    };

    if (instant) {
      var had = this.root.classList.contains('is-ready');
      this.root.classList.remove('is-ready');
      apply(this.thumb);
      // 强制回流后再恢复过渡，避免首帧从 (0,0) 滑过来
      void this.thumb.offsetWidth;
      if (had) this.root.classList.add('is-ready');
    } else {
      apply(this.thumb);
    }
  };

  /* 图 + 选中胶囊同步切到 i */
  AmadaCarousel.prototype.sync = function (i, first) {
    if (i < 0 || i >= this.count) return;
    this.index = i;

    for (var n = 0; n < this.count; n++) {
      var on = n === i;
      this.slides[n].classList.toggle('is-active', on);
      this.slides[n].setAttribute('aria-hidden', on ? 'false' : 'true');
      this.tabs[n].setAttribute('aria-selected', on ? 'true' : 'false');
      this.tabs[n].setAttribute('tabindex', on ? '0' : '-1');
    }

    this.placeThumb(!!first);

    if (first) {
      var self = this;
      // 先摆好滑块再开过渡，否则第一次会从左上角滑进来
      requestAnimationFrame(function () {
        self.root.classList.add('is-ready');
      });
    } else {
      this.scrollTabIntoView();
    }
  };

  /* 移动端 tag 条是横向滚动的，自动播放切到屏幕外的按钮时要把它滚进来 */
  AmadaCarousel.prototype.scrollTabIntoView = function () {
    var box = this.tabsBox;
    var tab = this.tabs[this.index];
    if (!box || !tab) return;
    if (box.scrollWidth <= box.clientWidth + 1) return; // 不可滚动，PC 情况

    var target = tab.offsetLeft - (box.clientWidth - tab.offsetWidth) / 2;
    var max = box.scrollWidth - box.clientWidth;
    target = Math.max(0, Math.min(target, max));
    if (typeof box.scrollTo === 'function') {
      box.scrollTo({ left: target, behavior: prefersReduced() ? 'auto' : 'smooth' });
    } else {
      box.scrollLeft = target;
    }
  };

  AmadaCarousel.prototype.start = function () {
    if (!this.autoplay) return;
    if (prefersReduced()) return;
    if (document.hidden || this.hovering || this.focusing) return;
    if (this.timer) return;
    var self = this;
    this.timer = setInterval(function () {
      self.sync((self.index + 1) % self.count);
    }, this.interval);
  };

  AmadaCarousel.prototype.stop = function () {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  AmadaCarousel.prototype.restart = function () {
    this.stop();
    this.start();
  };

  AmadaCarousel.prototype.destroy = function () {
    this.stop();
    if (this._ro) this._ro.disconnect();
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
  };

  var registry = [];

  function initAll(scope) {
    var host = scope || document;
    var list = host.querySelectorAll ? host.querySelectorAll('[data-amada-carousel]') : [];
    Array.prototype.forEach.call(list, function (root) {
      if (root.dataset.acsInit === '1') return;
      root.dataset.acsInit = '1';
      registry.push(new AmadaCarousel(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* ── 主题编辑器：增删/重排 block、改设置都会重渲染 section ── */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    registry = registry.filter(function (inst) {
      if (e.target.contains(inst.root)) {
        inst.destroy();
        return false;
      }
      return true;
    });
  });
  /* 编辑器里选中某个 block 时，直接把对应那张图切出来 */
  document.addEventListener('shopify:block:select', function (e) {
    var btn = e.target.querySelector
      ? e.target.querySelector('[data-acs-tab]')
      : null;
    var idx = e.target.getAttribute && e.target.getAttribute('data-acs-slide');
    registry.forEach(function (inst) {
      if (!inst.root.contains(e.target)) return;
      var i = idx !== null && idx !== undefined ? parseInt(idx, 10) : NaN;
      if (isNaN(i) && btn) i = parseInt(btn.getAttribute('data-acs-tab'), 10);
      if (!isNaN(i)) {
        inst.sync(i);
        inst.stop();
      }
    });
  });
  document.addEventListener('shopify:block:deselect', function () {
    registry.forEach(function (inst) {
      inst.start();
    });
  });
})();
